import { localSettingsService } from '../services/localSettingsService';
import { EmailUser } from 'titus-ts/dist/js/Mail/Recipient';
import { IExchangeRequestGenerator } from './OutlookApi';
import { AttachmentOffice } from './AttachmentOffice';
import { DictionaryInterface } from 'titus-ts/dist/js/Common/Util/DictionaryInterface';
import { MsalAuthService } from '../services/auth/msalAuthService';
import {
    MsalAccountIdentityHints,
    OfficeIdentityContextResolver,
} from '../services/auth/officeIdentityContextResolver';

interface EmailHeaders {
    [key: string]: string;
}

export class OutlookExhangeOnline implements IExchangeRequestGenerator {
    private graphEndpoint = '';
    private msalAuthService: MsalAuthService | undefined = undefined;

    private async init() {
        const useNestedAppAuth = localSettingsService.getLocalSettings().useNestedAppAuth;
        this.graphEndpoint = localSettingsService.getLocalSettings().graphEndpoint;
        const identityResolver = new OfficeIdentityContextResolver();
        const identityHints: MsalAccountIdentityHints = await identityResolver.resolve();

        this.msalAuthService = new MsalAuthService(identityHints);

        await this.msalAuthService.init(useNestedAppAuth);
    }

    private getNaaToken = async (): Promise<string> => {
        try {
            if (this.msalAuthService === undefined) {
                await this.init();
            }
        } catch (error) {
            console.error('There was a problem loading the auth service:', error);
            return '';
        }

        if (this.msalAuthService) {
            return this.msalAuthService.getGraphAccessToken().catch((error) => {
                console.error('Unable to acquire token:', error);
                return '';
            });
        }

        console.error('Auth Service could not be loaded.');
        return '';
    };

    private async getGraphRequestHeaders(): Promise<object> {
        return this.getNaaToken().then((graphToken) => {
            return {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${graphToken}`,
                    'Content-Type': 'application/json',
                },
            };
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private convertObjNameValueToRecord(headers: any): Record<string, string> {
        const record: { [key: string]: string } = {};
        if (headers) {
            for (const key in headers) {
                if (headers.hasOwnProperty(key)) {
                    record[headers[key].name] = headers[key].value;
                }
            }
        }
        return record;
    }

    private parseEmailHeaders(rawHeaders: string): EmailHeaders {
        const headers: EmailHeaders = {};
        const lines = rawHeaders.split(/\r?\n/);

        let currentHeader: string | null = null;
        lines.forEach((line) => {
            if (line.startsWith(' ') || line.startsWith('\t')) {
                // Continuation of the previous header
                if (currentHeader) {
                    headers[currentHeader] += ' ' + line.trim();
                }
            } else {
                // New header
                const [name, ...valueParts] = line.split(':');
                currentHeader = name.trim();
                headers[currentHeader] = valueParts.join(':').trim();
            }
        });
        return headers;
    }

    public setInternetHeaders(itemId: string, headers: DictionaryInterface<string>): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (Office.context.mailbox.item) {
                Office.context.mailbox.item.internetHeaders.setAsync(headers, { asyncContext: null }, (result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve();
                    } else {
                        reject(result.error.message);
                    }
                });
            } else {
                reject('Item is undefined');
            }
        });
    }

    public getAttachments(itemId: string): Promise<AttachmentOffice[]> {
        console.log('Get attachments from item: ', itemId);

        return new Promise<AttachmentOffice[]>((resolve, reject) => {
            const item = Office.context.mailbox.item;

            if (!item) {
                reject('Item is undefined');
                return;
            }

            const attachments: AttachmentOffice[] = [];

            // READ MODE (MessageRead / AppointmentRead)
            if ('attachments' in item && item.attachments) {
                const readAttachments = item.attachments as Office.AttachmentDetails[];

                readAttachments.forEach((attachment) => {
                    attachments.push(new AttachmentOffice(attachment.name, attachment.size ?? 0));
                });

                resolve(attachments);
                return;
            }

            // COMPOSE MODE (MessageCompose / AppointmentCompose)
            if ('getAttachmentsAsync' in item) {
                item.getAttachmentsAsync((result: Office.AsyncResult<Office.AttachmentDetailsCompose[]>) => {
                    if (result.status !== Office.AsyncResultStatus.Succeeded) {
                        reject(result.error);
                        return;
                    }

                    result.value.forEach((attachment) => {
                        attachments.push(new AttachmentOffice(attachment.name, attachment.size ?? 0));
                    });

                    resolve(attachments);
                });

                return;
            }

            reject('Attachments API not supported for this item type.');
        });
    }

    public getInternetHeaders(itemId: string, headers: string[]): Promise<Record<string, string>> {
        //READ_MODE
        if (Office.context.mailbox.item?.getAllInternetHeadersAsync !== undefined) {
            return new Promise<Record<string, string>>((resolve, reject) => {
                if (Office.context.mailbox.item) {
                    Office.context.mailbox.item.getAllInternetHeadersAsync({ asyncContext: null }, (result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            const headers = this.parseEmailHeaders(result.value);
                            resolve(headers);
                        } else {
                            reject(result.error);
                        }
                    });
                } else {
                    reject('Item is undefined');
                }
            });
            //COMPOSE_MODE
        } else if (Office.context.mailbox.item?.internetHeaders?.getAsync) {
            return new Promise<Record<string, string>>((resolve, reject) => {
                if (Office.context.mailbox.item) {
                    Office.context.mailbox.item.internetHeaders.getAsync(headers, { asyncContext: null }, (result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            resolve(result.value);
                        } else {
                            reject(result.error);
                        }
                    });
                } else {
                    reject('Items is undefined.');
                }
            });
        } else {
            return Promise.reject('Unable to retreive internet message headers..');
        }
    }

    public async getInReplyToByItemId(): Promise<string> {
        return Promise.resolve(Office.context.mailbox.item?.inReplyTo || '');
    }

    public async getOriginalItemSender(originalItemInternetId: string): Promise<EmailUser> {
        try {
            const options = await this.getGraphRequestHeaders();

            const query = this.getOriginalItemGraphQuery(originalItemInternetId);
            query.append('$select', 'from');

            const requestUri = `${this.graphEndpoint}/me/messages?${query.toString()}`;

            const response = await fetch(requestUri, options);

            if (!response.ok) {
                throw new Error(`getOriginalItemSender to GraphApi failed: ${response.statusText}`);
            }

            const data = await response.json();

            const address = data.value[0]?.from?.emailAddress?.address;
            const name = data.value[0]?.from?.emailAddress?.name;
            return new EmailUser(address, name);
        } catch (error) {
            console.error('There was a problem retrieving Original Sender..', error);
            return new EmailUser('', '');
        }
    }

    public async getOriginalItemInternetHeaders(originalItemInternetId: string): Promise<Record<string, string>> {
        try {
            const options = await this.getGraphRequestHeaders();

            const query = this.getOriginalItemGraphQuery(originalItemInternetId);
            query.append('$select', 'internetMessageHeaders');

            const requestUri = `${this.graphEndpoint}/me/messages?${query.toString()}`;

            const response = await fetch(requestUri, options);

            if (!response.ok) {
                throw new Error(
                    `getInternetHeadersFromInReplyTo to Graph API failed: ${response.status} ${response.statusText}`,
                );
            }

            const data = await response.json();
            const headers = data.value?.[0]?.internetMessageHeaders ?? [];

            console.log('Got InternetHeaders using Graph API via inReplyTo');
            return this.convertObjNameValueToRecord(headers);
        } catch (error) {
            console.error('There was a problem retrieving InternetHeaders via inReplyTo.', error);
            return {};
        }
    }

    private getOriginalItemGraphQuery(originalItemInternetId: string): URLSearchParams {
        // OData string literals escape single quotes by doubling them.
        const escapedInReplyTo = originalItemInternetId.replace(/'/g, "''");

        // if a user sends themselves a message the sent item and recieved item have the same internetMessageId
        // sort by created date desc to grab the recieved item as sent items dont have internetMessageHeaders yet
        return new URLSearchParams({
            $filter: `createdDateTime ge 1900-01-01T00:00:00Z and internetMessageId eq '${escapedInReplyTo}' and isDraft eq false`,
            $orderBy: 'createdDateTime desc',
            $top: '1',
        });
    }
}
